import { action, computed, makeObservable, observable, runInAction } from "mobx"

import { IStorageBackend, IUrlParams } from "@/domains/common/storage"
import { SortDirection, SortType } from "@/domains/table-processor"

export type ManualClView = "all-pools" | "my-positions"

/**
 * Version tag baked into every localStorage key this store owns. Today the
 * app only mounts a `"v1"` store, but a future `thick-v2` copy constructs
 * its own {@link ManualClPageStore} with `"v2"` so the two versions cannot
 * trample each other's saved filters / page size / view. Kept as a
 * specific string-literal union (rather than `string`) so the typechecker
 * flags a stray version before it lands in production.
 */
export type ManualClPageStoreVersion = "v1" | "v2"

// Type for sortBy object structure
export interface SortBy {
  key: string
  direction: SortDirection
  type: SortType
}

// Search param sources following the multi-source pattern
export interface SearchParamSources {
  urlParam: string | undefined
  localstorage: string | undefined
  default: string
  ui: string | undefined
}

// SortBy param sources
export interface SortByParamSources {
  urlParam: SortBy | undefined
  localstorage: SortBy | undefined
  default: SortBy
  ui: SortBy | undefined
}

// PageSize param sources
export interface PageSizeParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

// IsDetailed param sources
export interface IsDetailedParamSources {
  urlParam: boolean | undefined
  localstorage: boolean | undefined
  default: boolean
  ui: boolean | undefined
}

// CurrentPageNumber param sources
export interface CurrentPageNumberParamSources {
  urlParam: number | undefined
  localstorage: number | undefined
  default: number
  ui: number | undefined
}

// View param sources (Manual CL specific)
export interface ViewParamSources {
  urlParam: ManualClView | undefined
  localstorage: ManualClView | undefined
  default: ManualClView
  ui: ManualClView | undefined
}

export class ManualClPageStore {
  searchParamSources: SearchParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "",
    ui: undefined,
  }

  sortByParamSources: SortByParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: { key: "tvlUsd", direction: "desc", type: "number" },
    ui: undefined,
  }

  pageSizeParamSources: PageSizeParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 10,
    ui: undefined,
  }

  isDetailedParamSources: IsDetailedParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: false,
    ui: undefined,
  }

  currentPageNumberParamSources: CurrentPageNumberParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: 1,
    ui: undefined,
  }

  viewParamSources: ViewParamSources = {
    urlParam: undefined,
    localstorage: undefined,
    default: "all-pools",
    ui: undefined,
  }

  constructor(
    private storage: IStorageBackend,
    private urlParams: IUrlParams,
    private chainSlug: string,
    private version: ManualClPageStoreVersion = "v1",
  ) {
    makeObservable(this, {
      searchParamSources: observable,
      sortByParamSources: observable,
      pageSizeParamSources: observable,
      isDetailedParamSources: observable,
      currentPageNumberParamSources: observable,
      viewParamSources: observable,
      search: computed,
      sortBy: computed,
      pageSize: computed,
      isDetailed: computed,
      currentPageNumber: computed,
      view: computed,
      setSearchFromUrlParam: action,
      setSearchFromLocalStorage: action,
      setSearchFromUi: action,
      setSortByFromUrlParam: action,
      setSortByFromLocalStorage: action,
      setSortByFromUi: action,
      setPageSizeFromUrlParam: action,
      setPageSizeFromLocalStorage: action,
      setPageSizeFromUi: action,
      setIsDetailedFromUrlParam: action,
      setIsDetailedFromLocalStorage: action,
      setIsDetailedFromUi: action,
      setCurrentPageNumberFromUrlParam: action,
      setCurrentPageNumberFromLocalStorage: action,
      setCurrentPageNumberFromUi: action,
      setViewFromUrlParam: action,
      setViewFromLocalStorage: action,
      setViewFromUi: action,
    })
  }

  initialize(): void {
    runInAction(() => {
      this.initializeFromStorage()
      this.initializeFromUrlParams()
    })
  }

  /**
   * Build a versioned localStorage key, e.g.
   * `base-manual-cl-v1-search`. Co-locating the version with the suffix
   * keeps v1 and v2 storage partitions fully isolated — a concurrent
   * future `thick-v2` page store writing `-v2-search` will never collide
   * with v1's.
   */
  private storageKey(suffix: string): string {
    return `${this.chainSlug}-manual-cl-${this.version}-${suffix}`
  }

  /**
   * Read a value from the versioned key, falling back to the legacy
   * un-versioned key one time if the versioned slot is empty. When the
   * fallback hits, we write the value back into the new slot so that the
   * next read is a straight versioned hit and legacy storage becomes
   * effectively read-only for old clients.
   */
  private readWithLegacyFallback(suffix: string): string | undefined {
    const current = this.storage.getItem(this.storageKey(suffix))
    if (current !== undefined && current !== null) {
      return current
    }
    const legacyKey = `${this.chainSlug}-manual-cl-${suffix}`
    const legacy = this.storage.getItem(legacyKey)
    if (legacy === undefined || legacy === null) {
      return undefined
    }
    try {
      this.storage.setItem(this.storageKey(suffix), legacy)
    } catch {
      /* Storage quota / disabled — best-effort, keep reading from legacy */
    }
    return legacy
  }

  private initializeFromStorage(): void {
    const localSearch = this.readWithLegacyFallback("search")
    if (localSearch) {
      this.setSearchFromLocalStorage(localSearch)
    }

    const localSortBy = this.readWithLegacyFallback("sortBy")
    if (localSortBy) {
      try {
        const parsedSortBy = JSON.parse(localSortBy) as SortBy
        this.setSortByFromLocalStorage(parsedSortBy)
      } catch {
        // Invalid JSON, ignore
      }
    }

    const localPageSize = this.readWithLegacyFallback("pageSize")
    if (localPageSize) {
      const parsedPageSize = Number(localPageSize)
      if (!isNaN(parsedPageSize)) {
        this.setPageSizeFromLocalStorage(parsedPageSize)
      }
    }

    const localDetailed = this.readWithLegacyFallback("is-detailed")
    if (localDetailed) {
      const parsedDetailed = localDetailed === "true"
      this.setIsDetailedFromLocalStorage(parsedDetailed)
    }

    const localCurrentPageNumber = this.readWithLegacyFallback("currentPageNumber")
    if (localCurrentPageNumber) {
      const parsedCurrentPageNumber = Number(localCurrentPageNumber)
      if (!isNaN(parsedCurrentPageNumber)) {
        this.setCurrentPageNumberFromLocalStorage(parsedCurrentPageNumber)
      }
    }

    const localView = this.readWithLegacyFallback("view")
    if (localView && ["all-pools", "my-positions"].includes(localView)) {
      this.setViewFromLocalStorage(localView as ManualClView)
    }
  }

  public initializeFromUrlParams(): void {
    // Initialize search from URL params
    const urlSearch = this.urlParams.getParam("search")
    if (urlSearch) {
      this.setSearchFromUrlParam(urlSearch)
    }

    // Initialize sortBy from URL params (combining sortKey, sortDir, sortType)
    const urlSortKey = this.urlParams.getParam("sortKey")
    const urlSortDir = this.urlParams.getParam("sortDir")
    const urlSortType = this.urlParams.getParam("sortType")

    if (urlSortKey && urlSortDir && urlSortType) {
      const sortBy: SortBy = {
        key: urlSortKey,
        direction: urlSortDir as SortDirection,
        type: urlSortType as SortType,
      }
      this.setSortByFromUrlParam(sortBy)
    }

    // Initialize pageSize from URL params
    const urlPageSize = this.urlParams.getParam("pageSize")
    if (urlPageSize) {
      const parsedPageSize = Number(urlPageSize)
      if (!isNaN(parsedPageSize)) {
        this.setPageSizeFromUrlParam(parsedPageSize)
      }
    }

    // Initialize detailed from URL params (URL key is "detailed")
    const urlDetailed = this.urlParams.getParam("detailed")
    if (urlDetailed) {
      const parsedDetailed = urlDetailed === "true"
      this.setIsDetailedFromUrlParam(parsedDetailed)
    }

    // Initialize currentPageNumber from URL params (URL key is "currentPage")
    const urlCurrentPageNumber = this.urlParams.getParam("currentPage")
    if (urlCurrentPageNumber) {
      const parsedCurrentPageNumber = Number(urlCurrentPageNumber)
      if (!isNaN(parsedCurrentPageNumber)) {
        this.setCurrentPageNumberFromUrlParam(parsedCurrentPageNumber)
      }
    }

    // Initialize view from URL params (URL key is "view")
    const urlView = this.urlParams.getParam("view")
    if (urlView && ["all-pools", "my-positions"].includes(urlView)) {
      this.setViewFromUrlParam(urlView as ManualClView)
    }
  }

  get search(): string {
    return (
      this.searchParamSources.ui ??
      this.searchParamSources.urlParam ??
      this.searchParamSources.localstorage ??
      this.searchParamSources.default
    )
  }

  get sortBy(): SortBy {
    return (
      this.sortByParamSources.ui ??
      this.sortByParamSources.urlParam ??
      this.sortByParamSources.localstorage ??
      this.sortByParamSources.default
    )
  }

  get pageSize(): number {
    return (
      this.pageSizeParamSources.ui ??
      this.pageSizeParamSources.urlParam ??
      this.pageSizeParamSources.localstorage ??
      this.pageSizeParamSources.default
    )
  }

  get isDetailed(): boolean {
    return (
      this.isDetailedParamSources.ui ??
      this.isDetailedParamSources.urlParam ??
      this.isDetailedParamSources.localstorage ??
      this.isDetailedParamSources.default
    )
  }

  get currentPageNumber(): number {
    return (
      this.currentPageNumberParamSources.ui ??
      this.currentPageNumberParamSources.urlParam ??
      this.currentPageNumberParamSources.localstorage ??
      this.currentPageNumberParamSources.default
    )
  }

  get view(): ManualClView {
    return (
      this.viewParamSources.ui ??
      this.viewParamSources.urlParam ??
      this.viewParamSources.localstorage ??
      this.viewParamSources.default
    )
  }

  // Search setters
  setSearchFromUrlParam(value: string | undefined): void {
    if (!value) return
    if (this.searchParamSources.urlParam === value) return
    this.searchParamSources.urlParam = value
  }

  setSearchFromLocalStorage(value: string | undefined): void {
    if (!value) return
    if (this.searchParamSources.localstorage === value) return
    this.searchParamSources.localstorage = value
  }

  setSearchFromUi(value: string | undefined): void {
    if (value === undefined) return
    if (this.searchParamSources.ui === value) return
    this.searchParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(this.storageKey("search"), value)
    this.urlParams.setParam("search", value)
  }

  // SortBy setters
  setSortByFromUrlParam(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.urlParam?.key === value.key &&
      this.sortByParamSources.urlParam?.direction === value.direction &&
      this.sortByParamSources.urlParam?.type === value.type
    )
      return
    this.sortByParamSources.urlParam = value
  }

  setSortByFromLocalStorage(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.localstorage?.key === value.key &&
      this.sortByParamSources.localstorage?.direction === value.direction &&
      this.sortByParamSources.localstorage?.type === value.type
    )
      return
    this.sortByParamSources.localstorage = value
  }

  setSortByFromUi(value: SortBy | undefined): void {
    if (!value) return
    if (
      this.sortByParamSources.ui?.key === value.key &&
      this.sortByParamSources.ui?.direction === value.direction &&
      this.sortByParamSources.ui?.type === value.type
    )
      return
    this.sortByParamSources.ui = value

    // Auto-sync to storage (spread sortBy into individual URL params)
    this.storage.setItem(this.storageKey("sortBy"), JSON.stringify(value))
    this.urlParams.setParam("sortKey", value.key)
    this.urlParams.setParam("sortDir", value.direction)
    this.urlParams.setParam("sortType", value.type)
  }

  // PageSize setters
  setPageSizeFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.urlParam === value) return
    this.pageSizeParamSources.urlParam = value
  }

  setPageSizeFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.localstorage === value) return
    this.pageSizeParamSources.localstorage = value
  }

  setPageSizeFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.pageSizeParamSources.ui === value) return
    this.pageSizeParamSources.ui = value

    // Auto-sync to storage
    this.storage.setItem(this.storageKey("pageSize"), value.toString())
    this.urlParams.setParam("pageSize", value.toString())
  }

  // IsDetailed setters
  setIsDetailedFromUrlParam(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.urlParam === value) return
    this.isDetailedParamSources.urlParam = value
  }

  setIsDetailedFromLocalStorage(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.localstorage === value) return
    this.isDetailedParamSources.localstorage = value
  }

  setIsDetailedFromUi(value: boolean | undefined): void {
    if (value === undefined) return
    if (this.isDetailedParamSources.ui === value) return
    this.isDetailedParamSources.ui = value

    // Auto-sync to storage (URL key is "detailed")
    this.storage.setItem(this.storageKey("is-detailed"), value.toString())
    this.urlParams.setParam("detailed", value.toString())
  }

  // CurrentPageNumber setters
  setCurrentPageNumberFromUrlParam(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.urlParam === value) return
    this.currentPageNumberParamSources.urlParam = value
  }

  setCurrentPageNumberFromLocalStorage(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.localstorage === value) return
    this.currentPageNumberParamSources.localstorage = value
  }

  setCurrentPageNumberFromUi(value: number | undefined): void {
    if (value === undefined) return
    if (this.currentPageNumberParamSources.ui === value) return
    this.currentPageNumberParamSources.ui = value

    // Auto-sync to storage (URL key is "currentPage")
    this.storage.setItem(this.storageKey("currentPageNumber"), value.toString())
    this.urlParams.setParam("currentPage", value.toString())
  }

  // View setters
  setViewFromUrlParam(value: ManualClView | undefined): void {
    if (!value) return
    if (this.viewParamSources.urlParam === value) return
    this.viewParamSources.urlParam = value
  }

  setViewFromLocalStorage(value: ManualClView | undefined): void {
    if (!value) return
    if (this.viewParamSources.localstorage === value) return
    this.viewParamSources.localstorage = value
  }

  setViewFromUi(value: ManualClView | undefined): void {
    if (!value) return
    if (this.viewParamSources.ui === value) return
    this.viewParamSources.ui = value

    // Auto-sync to storage (URL key is "view")
    this.storage.setItem(this.storageKey("view"), value)
    this.urlParams.setParam("view", value)
  }
}
